import { addMonths, isAfter } from 'date-fns'
import { useLocalObservable } from 'mobx-react-lite'
import { useCallback, useContext, useEffect, useState } from 'react'

import StoresContext from '@/utils/StoresContext'

export const LOOKING_FOR_AVAILABILITIES_MAX_MONTHS = 3
const LOOKING_FOR_AVAILABILITIES_MIN_OPEN_DAYS = 10

interface UseFetchNextAvailabilitiesLocalState {
  hasLoadedNextAvailabilities: boolean
  hasLoadedFirstAvailableDate: boolean
  monthLoaded: string[]
}

interface UseFetchNextAvailabilitiesReturn {
  isLoadingFirstAvailableDate: boolean
  reloadFirstAvailableDate: () => void
}

const useFetchNextAvailabilities = (): UseFetchNextAvailabilitiesReturn => {
  const { appStore } = useContext(StoresContext)
  const { selectedHighlightedOffer } = appStore.state

  /**
   * function to determine if we need to compute the first available date in the current store context
   */
  const shouldComputeFirstAvailableDate = useCallback(
    () =>
      appStore.state.canAutomaticallyChangeDate &&
      (appStore.state.selectedHighlightedOffer
        ? !appStore.state.isSummaryDateCompatibleWithSelectedHighlightedOffer(appStore.state.wish.day)
        : !appStore.state.isCurrentDayAvailableForCurrentPax || appStore.state.isCurrentDayFinished),
    [appStore]
  )

  // react states that should trigger UI updates
  const [isLoadingFirstAvailableDate, setIsLoadingFirstAvailableDate] = useState(shouldComputeFirstAvailableDate())
  const [isFetchingExperienceMonth, setIsFetchingExperienceMonth] = useState(false)

  // internal observable state to keep track of what has been loaded
  const localStore = useLocalObservable<UseFetchNextAvailabilitiesLocalState>(() => ({
    hasLoadedNextAvailabilities: false,
    hasLoadedFirstAvailableDate: !shouldComputeFirstAvailableDate(),
    monthLoaded: []
  }))

  const stopSearchingForFirstAvailableDate = useCallback(() => {
    if (!localStore.hasLoadedFirstAvailableDate) {
      setIsLoadingFirstAvailableDate(false)
    }
  }, [localStore])

  const selectFirstAvailableDate = useCallback(
    (date: string) => {
      localStore.hasLoadedFirstAvailableDate = true
      appStore.selectWishDay(date).then(() => {
        setIsLoadingFirstAvailableDate(false)
      })
    },
    [appStore, localStore]
  )

  /**
   * wrapper function to fetch availability summary for a given month/year if not already fetched
   */
  const getAvailabilitiesForDate = useCallback(
    async (year: string | number, month: string | number) => {
      const isMonthLoaded = localStore.monthLoaded.includes(`${year}-${month}`)
      if (!isMonthLoaded) {
        localStore.monthLoaded.push(`${year}-${month}`)
        await appStore.getAvailabilitiesSummary(month, year)
      }
    },
    // these dependencies should never change
    [appStore, localStore]
  )

  /**
   * Recursive function to fetch next months until we find enough open days or reach max months limit, starting from month of current wish.day
   */
  const getNextAvailabilities = useCallback(
    async (next = 0) => {
      if (next >= LOOKING_FOR_AVAILABILITIES_MAX_MONTHS) {
        stopSearchingForFirstAvailableDate()
        return
      }
      const wishDay = appStore.state.wish.day
      const nextMonth = addMonths(wishDay, next).getMonth() + 1
      const yearOfNextMonth = addMonths(wishDay, next).getFullYear()

      await getAvailabilitiesForDate(yearOfNextMonth, nextMonth)

      // checking updated nextAvailableDates from appState when getAvailabilitiesForDate is done
      if (appStore.state.nextAvailableDates.length < LOOKING_FOR_AVAILABILITIES_MIN_OPEN_DAYS) {
        // continue searching next month
        getNextAvailabilities(next + 1)
      } else {
        stopSearchingForFirstAvailableDate()
      }
    },
    // these dependencies should never change
    [appStore.state, getAvailabilitiesForDate, stopSearchingForFirstAvailableDate]
  )

  /**
   * Effect to fetch availability summaries depending on context
   * it should be called only once when the component is mounted
   * or when the user asks to reload the first available date, in case context has changed at that time
   */
  useEffect(() => {
    // we need to have up-to-date values from appState in the effect, but we don't want to trigger the effect when they change
    const { selectedHighlightedOffer, nextAvailableDates } = appStore.state

    if (
      !localStore.hasLoadedNextAvailabilities &&
      nextAvailableDates.length < LOOKING_FOR_AVAILABILITIES_MIN_OPEN_DAYS
    ) {
      // set hasLoadedNextAvailabilities to true to call this effect only once
      localStore.hasLoadedNextAvailabilities = true
      if (selectedHighlightedOffer) {
        const date = selectedHighlightedOffer.date_from
        const [year, month] = date.split('-')
        const startsAfterNow = isAfter(new Date(date), new Date())
        if (startsAfterNow) {
          ;(async () => {
            setIsFetchingExperienceMonth(true)
            await getAvailabilitiesForDate(year, month)
            setIsFetchingExperienceMonth(false)
          })()
        }
      }
      getNextAvailabilities()
    }
  }, [
    // values that we need to update the effect loop - they should not change, hence not trigger a new search
    appStore,
    localStore,
    stopSearchingForFirstAvailableDate,
    getNextAvailabilities,
    getAvailabilitiesForDate,
    // only value that should trigger a new search for first available date:
    localStore.hasLoadedNextAvailabilities,
    appStore.state.nextAvailableDates.length
  ])

  const nextAvailableDate = appStore.state.nextAvailableDateAtIndex(0)

  /**
   * Effect to select first available date when encountered,
   * when we are in a context that allows it
   * it should be called only once when the component is mounted
   * or when the user asks to reload the first available date, in case context has changed at that time
   */
  useEffect(() => {
    if (
      shouldComputeFirstAvailableDate() &&
      nextAvailableDate !== undefined &&
      !localStore.hasLoadedFirstAvailableDate
    ) {
      selectFirstAvailableDate(nextAvailableDate)
    }
  }, [
    localStore,
    nextAvailableDate,
    selectFirstAvailableDate,
    selectedHighlightedOffer,
    shouldComputeFirstAvailableDate
  ])

  return {
    isLoadingFirstAvailableDate: isLoadingFirstAvailableDate || isFetchingExperienceMonth,
    reloadFirstAvailableDate: () => {
      if (shouldComputeFirstAvailableDate()) {
        setIsLoadingFirstAvailableDate(true)
        localStore.hasLoadedFirstAvailableDate = false
        localStore.hasLoadedNextAvailabilities = false
      }
    }
  }
}

export default useFetchNextAvailabilities
